import { IMapMngr, IMapClickHandler } from '../interfaces';
import { AspObject } from '../AspObject';
import { MapTool } from './MapTool';
import * as aspSettings from '../aspSettings';
import * as units from '../../common/units';
import * as settings from '../../common/settings';

declare var NeWMI;

export class SpecialMeasurmentsTool extends NeWMI.Tool.DistanceTool implements IMapClickHandler {

  private static mapEvents = [
    NeWMI.Map.Enums.EMapEvents.MouseDown
  ];

  private displayUnits: settings.DisplayUnits;

  private mapTool: MapTool;

  private fromDefined = false;
  private fromObject: AspObject = null;
  private toObject: AspObject = null;

  /**
  * @constructor
  * Creates new SpecialMeasurmentsTool instance
  */
  constructor(mapMngr: IMapMngr) {
    super(...arguments);

    this.caption = "EURO_N Special Measurments Tool";
    this.id = SpecialMeasurmentsTool.ID;
    this.bgLayer.color = "rgba(0, 0, 0, 0)";

    this.setMinimumDistanceText(1);
    this.setTextStyle({ font: aspSettings.NORMAL_FONT });

    this.mapTool = new MapTool(mapMngr, SpecialMeasurmentsTool.mapEvents);
    this.mapTool.setMapClickHandler(this, undefined, true /* allow single object click */);
  }

  /**
  * @method init
  * This method is called automatically when adding the tool to the NeWMI.Tool.ToolsManager
  * @param {NeWMI.Map.Base.ABasicMap} The map of the tool
  */
  init(p_objMap) {
    super.init(p_objMap);
  }

  setUnits() {
    if (this.displayUnits != settings.userSettings.displayUnits) {
      this.displayUnits = settings.userSettings.displayUnits;
      if (this.displayUnits == settings.DisplayUnits.Metric) {
        this.setLengthSystemStyle(NeWMI.Tool.DistanceTool.LengthSystems.metric);
        this.setUnitsText({ microText: units.M_LABEL.toLowerCase(), macroText: units.KM_LABEL.toLowerCase() });
      }
      else {
        this.setLengthSystemStyle(NeWMI.Tool.DistanceTool.LengthSystems.nmile);
        this.setUnitsText({ microText: units.FT_LABEL.toLowerCase(), macroText: units.NM_LABEL.toLowerCase() });
      }
    }
  }

  activate(arg) {
    // set cursor
    this.mapTool.mapMngr.setCursor('crosshair');
    this.setUnits();
    super.activate(arg);
    if (this.fromDefined)
      this.initFromPoint();
  }

  setFromObject(obj: AspObject) {
    this.fromObject = obj;

    if (obj) {
      let dispObj = this.mapTool.mapMngr.getObject(obj.id);
      if (!dispObj.isVisible())
        this.finish(true);
      else
        this.fromDefined = true;
    }
  }

  setToObject(obj: AspObject) {
    this.toObject = obj;
  }

  refresh() {
    this.setUnits();
    
    if (this.fromObject) {
      if (!this.fromObject.active())
        this.finish(true);

      let dispObj = this.mapTool.mapMngr.getObject(this.fromObject.id);
      if (!dispObj.isVisible())
        this.finish(true);
      
      if (this._feedback && this._feedback._geometry && this._feedback._geometry.points && this._feedback._geometry.points.length >= 1) {
        this._feedback._geometry.points[0] = dispObj.getPresentationPoint();
        this._feedbackLayer.refresh();
      }
    }
  }

  initFromPoint() {
    let dispObj = this.mapTool.mapMngr.getObject(this.fromObject.id);
    let dispObjPoint = dispObj.getPresentationPoint();

    super.onMapEvent({
      eventType: NeWMI.Map.Enums.EMapEvents.MouseDown,
      map: this.map,
      mapPoint: dispObjPoint
    });

    if (this._feedback && this._feedback._geometry && this._feedback._geometry.points && this._feedback._geometry.points.length == 0) {
      this._feedback._geometry.points.push(dispObjPoint);
      this._feedback._geometry.points.push(this.mapTool.mapMngr.mouseMapLocation());
      this._feedbackLayer.refresh();
    }
  }

  _raiseAddMeasurment(objNewGeometry) {
    const objOnEditDrag = new NeWMI.EventObject(this);

    objOnEditDrag.type = "addMeasure";
    objOnEditDrag.geometry = objNewGeometry;
    objOnEditDrag.fromObjectId = this.fromObject ? this.fromObject.id : null;
    objOnEditDrag.toObjectId = this.toObject ? this.toObject.id : null;
    objOnEditDrag.mapMngr = this.mapTool.mapMngr;

    objOnEditDrag.raise();
  }

  finish(inForceFinish) {
    super.finish(inForceFinish);
    if (!inForceFinish) {
      this._feedbackLayer.end();
      this._feedbackLayer.refresh(this.map);

      this._raiseAddMeasurment(this._feedback._geometry);
      if (this._feedback)
        this._feedback._geometry = null;
    }
    else {
      this._feedback = null;
    }
    this.fromDefined = false;
    this.fromObject = null;
    this.toObject = null;
  }

  onMapEvent(evt) {
    let finished = false;
    if (evt.eventType == NeWMI.Map.Enums.EMapEvents.MouseDblClick) {
      return true;
    }
    else if (evt.eventType == NeWMI.Map.Enums.EMapEvents.MouseDown) {
      if (this.fromDefined) {
        if (evt.button == 0) {
          // left mouse button click ends the ruler
          if (this.mapTool.handleMapEvent(evt)) {
            // will proceed with onObjectsClicked()
            return true;
          }
          evt.eventType = NeWMI.Map.Enums.EMapEvents.MouseDblClick;
          finished = true;
        }
        else if (evt.button == 1) {
          // middle button click adds a segment
          evt.button = 0;
        }
      }
      if (evt.button == 2) {
        // prevent cursor change
        return false;
      }
    }

    if (!finished && evt.eventType == NeWMI.Map.Enums.EMapEvents.MouseDown) {
      if (this.mapTool.handleMapEvent(evt))
        return true;
    }

    super.onMapEvent(evt);

    if (!finished) {
      if (!this.fromDefined)
        this.fromDefined =
          this._feedback && this._feedback._geometry && this._feedback._geometry.points && this._feedback._geometry.points.length >= 1 ? true : false;
      if (evt.eventType == NeWMI.Map.Enums.EMapEvents.MouseUp) {
        // restore cursor
        this.mapTool.mapMngr.setCursor('crosshair');
      }
    }
  }

  onObjectsClicked(objects: AspObject[]) {
    if (objects.length == 1) {
      if (this.fromDefined) {
        this.setToObject(objects[0]);
        this.finish(false);
      }
      else {
        this.setFromObject(objects[0]);
        setTimeout(() => this.initFromPoint(), 100); // allow map mouse enter event, to establish current mouse location
      }
    }
  }

  static get ID() {
    return "Tool_EURO_N_SpecialMeasurmentsTool";
  }

}            
